// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

const SERIES_COLOR = '#7c4ea5';
const TICKER = 'FUN';

// Right margin is sized for the longest end label the feed can produce
// ("+10.00 (+100.00%)"), not today's. Same at every breakpoint.
const margins = {
  top: 20,
  right: 118,
  bottom: 25,
  left: 32,
};

let rawData;
let chartData; // history through the day before the live point
let currentData; // live (or fallback) quote
let lastData;
let peak;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    // Headline and dek are written from live data; skip the Google Doc values.
    const skip = ['headline', 'subhed'];
    Object.keys(meta).filter(id => !skip.includes(id)).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;

stylePremium(CNNPolitics, CNNBusiness);

// Two-step rounding so 3.15 -> 3.2 and 14.095 -> 14.10
const round2 = v => Math.round(v * 100) / 100;
const fixed = v => d3.format(',.2f')(round2(v));

const ET = 'America/New_York';

// Format AM/PM to a.m./p.m.
function formatAmPm(text) {
  return text.replace(/AM/g, 'a.m.').replace(/PM/g, 'p.m.');
}

// 'open' | 'pre' | 'closed' (weekends count as closed; holidays aren't handled)
function getMarketState() {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: ET }));
  const day = et.getDay();
  const minutes = et.getHours() * 60 + et.getMinutes();

  if (day === 0 || day === 6 || minutes >= 16 * 60) return 'closed';
  if (minutes < 9 * 60 + 30) return 'pre';
  return 'open';
}

function formatEasternTime(date) {
  const state = getMarketState();
  if (state === 'closed') return 'Last close';
  if (state === 'pre') return 'Pre-market';

  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: ET,
  });
  return formatAmPm(`${timeString} ET`);
}

function formatFootnoteDateTime(date) {
  const dateTimeString = date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: ET,
  });
  return formatAmPm(`${dateTimeString} ET`);
}

function updateFootnote(date) {
  const footnoteElement = document.querySelector('.footnote');
  if (footnoteElement) {
    footnoteElement.textContent = `Last updated: ${formatFootnoteDateTime(date)}`;
  }
}

const etDay = date => date.toLocaleDateString('en-CA', { timeZone: ET }); // YYYY-MM-DD
const fmtDay = d3.timeFormat('%Y-%m-%d');
const fmtMonthYear = d3.timeFormat('%b %Y');

// The dek is built from data, not the Google Doc. The headline is static in index.html.
function updateText() {
  if (!currentData) return;
  const { close, date, percentChange } = currentData;

  const dek = document.getElementById('dek');

  // "Today" only if the quote is from the current ET day during the session
  const live = getMarketState() !== 'closed' && etDay(date) === etDay(new Date());
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long', timeZone: ET });
  const verb = live ? 'are trading at' : 'closed at';
  const price = `<span class="stat-pill">$${fixed(close)}</span>${live ? '' : ` on ${weekday}`}`;
  const baseline = live ? 'since the previous close' : 'from the previous close';

  const parts = [];

  // Daily move: whole percent at 10+, one decimal below
  if (percentChange != null) {
    const abs = Math.abs(percentChange);
    const text = abs >= 10
      ? `${Math.round(round2(abs))}`
      : (Math.round(round2(abs) * 10) / 10).toFixed(1);
    parts.push(Number(text) === 0
      ? `little changed ${baseline}`
      : `${percentChange > 0 ? 'up' : 'down'} ${text}% ${baseline}`);
  }

  // Distance from the three-year high
  if (peak) {
    if (close >= peak.close) {
      parts.push('at a three-year high');
    } else {
      const drop = Math.round(round2((1 - close / peak.close) * 100));
      parts.push(`${drop}% below their three-year high in ${fmtMonthYear(peak.date)}`);
    }
  }

  dek.innerHTML = `Shares ${verb} ${price}${parts.length ? `, ${parts.join(' and ')}` : ''}.`;
}

// Live quote. Returns true if currentData changed.
async function fetchMarketData() {
  const response = await fetch(`https://production.dataviz.cnn.io/insights/stock_price_change/${TICKER}`);
  const data = await response.json();

  if (!data) return false;

  const next = {
    date: new Date(data.last_updated),
    close: data.current_price,
    prevClose: data.prev_close_price,
    change: data.price_change_from_prev_close,
    percentChange: data.percent_change_from_prev_close, // already a percentage
  };

  if (lastData && next.date <= lastData.date && next.close === lastData.close) return false;

  currentData = next;
  lastData = next;
  return true;
}

async function refresh() {
  try {
    if (await fetchMarketData()) {
      updateFootnote(currentData.date);
      updateText();
      draw();
    }
  } catch (e) {
    // keep the last good state
  }
}

function prepareSeries() {
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 3);

  // History stops before the live day so the line ends on the live point
  const liveDay = etDay(currentData.date);
  chartData = rawData.filter(d => d.date > cutoff && fmtDay(d.date) < liveDay);
  peak = chartData.reduce((best, d) => (!best || d.close > best.close ? d : best), null);
}

// Initial load
d3.json('https://ix.cnn.io/data/stocks/fun.json').then(async (response) => {
  rawData = response.data.map(d => ({
    date: d3.timeParse('%Y-%m-%d')(d.date),
    close: +d.close,
  }));

  try {
    await fetchMarketData();
  } catch (e) {
    // fall through to the feed's own latest price
  }

  if (!currentData) {
    // Live quote failed: show price and date only, no change figures
    currentData = {
      date: new Date(response.lastUpdated),
      close: response.latestPrice,
      change: null,
      percentChange: null,
    };
    lastData = currentData;
  }

  prepareSeries();
  updateFootnote(currentData.date);
  updateText();

  draw();
  window.onresize = draw;

  setInterval(refresh, 30000); // Update every 30 seconds
});

function draw() {
  if (!chartData || !currentData) return;

  prepareSeries();

  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const minDomain = d3.min(chartData, d => d.date);
  const maxDomain = currentData.date;

  const series = [...chartData, { date: currentData.date, close: currentData.close }];

  const dataMax = d3.max(series, d => d.close);
  const maxValue = Math.ceil(dataMax + (dataMax * 0.1));

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([0, maxValue])
    .range([chartHeight, 0]);

  // Desktop: a label per January. Mobile: first and last only.
  const xAxis = d3.axisBottom()
    .scale(xScale)
    .tickSize(0)
    .tickPadding(8)
    .tickValues(isMobile
      ? [minDomain, maxDomain]
      : d3.timeYear.range(d3.timeYear.ceil(minDomain), maxDomain))
    .tickFormat(d => (isMobile ? fmtMonthYear(d) : d3.timeFormat('%Y')(d)));

  // Units on the top tick only
  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(isMobile ? 3 : 6)
    .tickSize(-chartWidth)
    .tickPadding(6)
    .tickFormat((d, i, nodes) => (i === nodes.length - 1 ? `$${d}` : d));

  const lineGen = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.close));

  const areaGen = d3.area()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y0(chartHeight)
    .y1(d => yScale(d.close));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height)
    .attr('role', 'img')
    .attr('aria-label', `Line chart of Six Flags' closing stock price over three years. Latest price: $${fixed(currentData.close)}.`);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  const xg = g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  if (isMobile) {
    xg.selectAll('.tick text').attr('text-anchor', (d, i) => (i === 0 ? 'start' : 'end'));
  }

  const yg = g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  yg.select('.domain').remove();
  yg.selectAll('.tick').filter(d => d === 0).classed('zero', true);

  // Area under the line, then the line
  g.append('path')
    .datum(series)
    .attr('class', 'area')
    .attr('fill', SERIES_COLOR)
    .attr('d', areaGen);

  g.append('path')
    .datum(series)
    .attr('class', 'series-line')
    .attr('fill', 'none')
    .attr('stroke', SERIES_COLOR)
    .attr('stroke-width', isMobile ? 1.5 : 2.5)
    .attr('stroke-linejoin', 'round')
    .attr('d', lineGen);

  // Three-year high callout (skipped when the live price is the high)
  if (peak && currentData.close < peak.close) {
    const px = xScale(peak.date);
    const py = yScale(peak.close);
    const flip = px > chartWidth * 0.6;

    const callout = g.append('g')
      .attr('class', 'peak')
      .attr('transform', `translate(${[px, py]})`);

    const text = callout.append('text')
      .attr('text-anchor', flip ? 'end' : 'start')
      .attr('x', flip ? -8 : 8)
      .attr('y', 4);

    text.append('tspan')
      .attr('class', 'peak-value')
      .text(`$${fixed(peak.close)}`);
    text.append('tspan')
      .attr('class', 'peak-date')
      .attr('dx', 5)
      .text(fmtMonthYear(peak.date));
  }

  // Live endpoint: pulse, dot, label
  const endX = xScale(currentData.date);
  const endY = yScale(currentData.close);

  const currentPoint = g.append('g')
    .attr('class', 'current-point')
    .attr('transform', `translate(${[endX, endY]})`);

  currentPoint.append('circle')
    .attr('class', 'pulse')
    .attr('r', 4)
    .attr('fill', SERIES_COLOR);

  currentPoint.append('circle')
    .attr('r', 4)
    .attr('fill', SERIES_COLOR)
    .attr('stroke', '#fff')
    .attr('stroke-width', 1.5);

  const annotation = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[endX, endY]})`);

  // Block of three lines, middle line centered on the dot
  const hasChange = currentData.change != null && currentData.percentChange != null;
  const lines = hasChange ? [-11, 4, 19] : [4, 19];

  annotation.append('text')
    .attr('class', 'annotation-price')
    .attr('x', 10)
    .attr('y', lines[0])
    .text(`$${fixed(currentData.close)}`);

  if (hasChange) {
    const up = currentData.change >= 0;
    const sign = up ? '+' : '';
    annotation.append('text')
      .attr('class', `annotation-change ${up ? 'up' : 'down'}`)
      .attr('x', 10)
      .attr('y', lines[1])
      .text(`${sign}${fixed(currentData.change)} (${sign}${fixed(currentData.percentChange)}%)`);
  }

  annotation.append('text')
    .attr('class', 'annotation-time')
    .attr('x', 10)
    .attr('y', lines[lines.length - 1])
    .text(formatEasternTime(currentData.date));

  window.pymChild.sendHeight();
}
